<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">الجدول الدراسي</h1>
          <p class="ds-page-header__subtitle">تحديد أيام وأوقات كل سنة دراسية — هذا الجدول هو أساس حساب الحضور والغياب</p>
        </div>
        <button class="ds-btn ds-btn--primary" :disabled="!filterYearId" v-tooltip.top="!filterYearId ? 'اختر السنة الدراسية أولاً' : ''" @click="openCreate">
          <i class="pi pi-plus"></i> إضافة موعد
        </button>
      </div>

      <!-- Filters -->
      <div class="row mb-3">
        <div class="col-md-4 col-12">
          <div class="form-field">
            <label class="field-label">السنة الدراسية</label>
            <select class="form-select" v-model="filterYearId" @change="onYearFilterChange">
              <option :value="null">اختر السنة الدراسية...</option>
              <option v-for="y in gradeStore.activeGrades" :key="y.id" :value="y.id">{{ y.name }}</option>
            </select>
          </div>
        </div>
        <div class="col-md-4 col-12">
          <div class="form-field">
            <label class="field-label">المجموعة</label>
            <select class="form-select" v-model="filterGroupId" :disabled="!filterYearId" @change="fetchSchedules">
              <option :value="null">الكل (مواعيد السنة والمجموعات)</option>
              <option :value="0">مواعيد السنة العامة فقط (بدون مجموعة)</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <Loading v-if="loading" />

      <template v-else-if="!filterYearId">
        <div class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-calendar"></i></div>
          <h3 class="empty-state__title">اختر السنة الدراسية</h3>
          <p class="empty-state__desc">لعرض وإدارة الجدول الدراسي، اختر السنة الدراسية أولاً.</p>
        </div>
      </template>

      <template v-else>
        <div v-if="!schedules.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-calendar-times"></i></div>
          <h3 class="empty-state__title">لا توجد مواعيد بعد</h3>
          <p class="empty-state__desc">أضف أول موعد لسنة "{{ currentYearName }}" — لا يشترط وجود مجموعة.</p>
          <button class="ds-btn ds-btn--primary mt-2" @click="openCreate">
            <i class="pi pi-plus"></i> إضافة موعد
          </button>
        </div>

        <div v-else class="ds-table-wrap table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th>المجموعة</th>
                <th class="text-center">اليوم</th>
                <th class="text-center">من</th>
                <th class="text-center">إلى</th>
                <th>المادة</th>
                <th class="text-center">الحالة</th>
                <th class="text-center">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in schedules" :key="s.id">
                <td>
                  <span v-if="s.group?.name">{{ s.group.name }}</span>
                  <span v-else class="badge-pill">كل السنة</span>
                </td>
                <td class="text-center">{{ dayLabel(s.day_of_week) }}</td>
                <td class="text-center" dir="ltr">{{ s.start_time }}</td>
                <td class="text-center" dir="ltr">{{ s.end_time }}</td>
                <td>{{ s.subject_name || '—' }}</td>
                <td class="text-center">
                  <button class="status-badge" :class="s.is_active ? 'status-badge--accepted' : 'status-badge--muted'" @click="toggle(s)">
                    {{ s.is_active ? 'مفعّل' : 'معطّل' }}
                  </button>
                </td>
                <td class="text-center">
                  <div class="icon-action-group justify-content-center">
                    <button class="icon-action-btn icon-action-btn--primary" v-tooltip.top="'تعديل'" @click="openEdit(s)"><i class="pi pi-pencil"></i></button>
                    <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف'" @click="remove(s)"><i class="pi pi-trash"></i></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>

  <!-- Create / Edit dialog -->
  <Dialog v-model:visible="formOpen" modal :style="{ width: '36rem' }" :breakpoints="{ '640px': '95vw' }" :header="editing ? 'تعديل الموعد' : 'إضافة موعد'">
    <div class="form-field">
      <label class="field-label">السنة الدراسية</label>
      <select class="form-select" v-model="form.academic_year_id" disabled>
        <option :value="filterYearId">{{ currentYearName }}</option>
      </select>
    </div>
    <div class="form-field mt-2">
      <label class="field-label">المجموعة <span class="optional">اختياري</span></label>
      <select class="form-select" v-model="form.group_id">
        <option :value="null">كل السنة (بدون مجموعة محددة)</option>
        <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
      </select>
      <p class="field-hint">اتركه بدون تحديد ليسري الموعد على السنة الدراسية بالكامل، أو اختر مجموعة ليخصّها فقط.</p>
    </div>
    <div class="ds-field-grid mt-2">
      <div class="form-field">
        <label class="field-label">اليوم</label>
        <select class="form-select" v-model="form.day_of_week">
          <option v-for="d in dayOptions" :key="d.value" :value="d.value">{{ d.label }}</option>
        </select>
      </div>
      <div class="form-field">
        <label class="field-label">الحالة</label>
        <select class="form-select" v-model="form.is_active">
          <option :value="true">مفعّل</option>
          <option :value="false">معطّل</option>
        </select>
      </div>
    </div>
    <div class="ds-field-grid mt-2">
      <div class="form-field">
        <label class="field-label">وقت البداية</label>
        <input type="time" class="form-control" v-model="form.start_time" />
      </div>
      <div class="form-field">
        <label class="field-label">وقت النهاية</label>
        <input type="time" class="form-control" v-model="form.end_time" />
      </div>
    </div>
    <div class="form-field mt-2">
      <label class="field-label">المادة <span class="optional">اختياري</span></label>
      <input type="text" class="form-control" v-model="form.subject_name" placeholder="مثال: رياضيات" />
    </div>
    <span class="text-danger d-block mt-2" v-if="formError">{{ formError }}</span>

    <template #footer>
      <button class="btn-cancel" @click="formOpen = false">إلغاء</button>
      <button class="ds-btn ds-btn--primary" :disabled="saving" @click="save">
        <i class="pi pi-check"></i> {{ saving ? 'جارٍ الحفظ...' : 'حفظ' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dialog from 'primevue/dialog';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

const DAY_OPTIONS = [
  { value: 0, label: 'الأحد' },
  { value: 1, label: 'الاثنين' },
  { value: 2, label: 'الثلاثاء' },
  { value: 3, label: 'الأربعاء' },
  { value: 4, label: 'الخميس' },
  { value: 5, label: 'الجمعة' },
  { value: 6, label: 'السبت' },
];

function blankForm(yearId, groupId) {
  return {
    id: null,
    academic_year_id: yearId,
    group_id: groupId ?? null,
    day_of_week: 0,
    start_time: '17:00',
    end_time: '19:00',
    subject_name: '',
    is_active: true,
  };
}

export default {
  name: 'GroupScheduleIndex',
  components: { Dialog, Breadcrumb, Loading },

  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'الجدول الدراسي', path: '/schedule' }],
      dayOptions: DAY_OPTIONS,

      filterYearId: null,
      filterGroupId: null,
      groups: [],
      schedules: [],
      loading: false,

      formOpen: false,
      editing: false,
      form: blankForm(null),
      formError: '',
      saving: false,
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },
    currentYearName() {
      return this.gradeStore.activeGrades.find(y => y.id === this.filterYearId)?.name ?? '';
    },
  },

  methods: {
    dayLabel(v) { return DAY_OPTIONS.find(d => d.value === v)?.label ?? v; },

    async onYearFilterChange() {
      this.filterGroupId = null;
      this.groups = [];
      this.schedules = [];
      if (!this.filterYearId) return;
      await this.loadGroups();
      await this.fetchSchedules();
    },

    async loadGroups() {
      try {
        const { data } = await axios.get(`/academic-years/${this.filterYearId}/groups`);
        this.groups = data.data ?? [];
      } catch {
        this.groups = [];
      }
    },

    async fetchSchedules() {
      if (!this.filterYearId) return;
      this.loading = true;
      try {
        const { data } = await axios.get('/group-schedules', {
          params: { academic_year_id: this.filterYearId, group_id: this.filterGroupId ?? undefined },
        });
        this.schedules = data.data ?? [];
      } catch {
        this.schedules = [];
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      if (!this.filterYearId) return;
      this.editing = false;
      this.formError = '';
      // A real group filter pre-selects that group; "all"/"year-level only"
      // default to a year-wide (group-less) slot.
      const defaultGroupId = this.filterGroupId > 0 ? this.filterGroupId : null;
      this.form = blankForm(this.filterYearId, defaultGroupId);
      this.formOpen = true;
    },

    openEdit(s) {
      this.editing = true;
      this.formError = '';
      this.form = {
        id: s.id,
        academic_year_id: s.academic_year_id,
        group_id: s.group_id,
        day_of_week: s.day_of_week,
        start_time: (s.start_time || '').slice(0, 5),
        end_time: (s.end_time || '').slice(0, 5),
        subject_name: s.subject_name || '',
        is_active: !!s.is_active,
      };
      this.formOpen = true;
    },

    async save() {
      this.formError = '';
      this.saving = true;
      try {
        const payload = { ...this.form };
        if (this.editing) {
          await axios.put(`/group-schedules/${this.form.id}`, payload);
        } else {
          await axios.post('/group-schedules', payload);
        }
        this.formOpen = false;
        await this.fetchSchedules();
        Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1400, showConfirmButton: false });
      } catch (e) {
        this.formError = e?.response?.data?.message || Object.values(e?.response?.data?.errors || {})[0]?.[0] || 'حدث خطأ أثناء الحفظ';
      } finally {
        this.saving = false;
      }
    },

    async toggle(s) {
      try {
        await axios.patch(`/group-schedules/${s.id}/toggle-active`);
        await this.fetchSchedules();
      } catch {
        Swal.fire({ icon: 'error', title: 'تعذّر تغيير الحالة', timer: 2000, showConfirmButton: false });
      }
    },

    async remove(s) {
      const { isConfirmed } = await Swal.fire({
        title: 'حذف الموعد؟', text: 'لا يمكن التراجع عن هذا الإجراء.', icon: 'warning',
        showCancelButton: true, confirmButtonText: 'حذف', cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!isConfirmed) return;
      try {
        await axios.delete(`/group-schedules/${s.id}`);
        await this.fetchSchedules();
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1400, showConfirmButton: false });
      } catch {
        Swal.fire({ icon: 'error', title: 'تعذّر الحذف', timer: 2000, showConfirmButton: false });
      }
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
  },
};
</script>

<style scoped>
</style>
